import { readInventoryField } from '../janus-engine/compile/inventory-raw-readers.js';
import { readItemField } from '../janus-engine/compile/item-field-reader.js';
import { objectVitalityTemplate, resolveObjectVitality, installObjectVitality } from '../janus-engine/inventory/object-vitality.js';
import { record, upper, type Row } from '../janus-engine/ability/context.js';

/** Keep each fresh instance/template SELECT for one resolution, including retries for other targets. */
export function createObjectVitalityFactReader(fetchObject: (id: string) => Promise<unknown>): (id: string) => Promise<unknown> {
  const reads = new Map<string, Promise<unknown>>();
  return id => {
    id = upper(id);
    let pending = reads.get(id);
    if (!pending) { pending = fetchObject(id); reads.set(id, pending); }
    return pending;
  };
}

/** Read the raw facet already held by this page without importing the facet runtime's state. */
function heldInventory(encounter: Row, id: string): Row | null {
  for (const key of ['_inventory_base', '_entity_base']) {
    const base = record(record(encounter[key])?.base);
    const rows = Array.isArray(base?.inventory) ? base.inventory : [];
    for (const entry of rows) {
      const row = record(entry);
      if (row && upper(readInventoryField(row, 'inventory_id')) === id) return row;
    }
  }
  return null;
}

/** A successful SELECT may establish zero vitality. A failed read never establishes any game fact. */
export async function hydrateInventoryObjectVitality(encounterOrRead: Row | (() => Row | null), id: string,
  definition: (id: string) => Row | null, fetchObject: (id: string) => Promise<unknown>, deadlineMs = 30_000): Promise<boolean> {
  id = upper(id);
  const encounterNow = (): Row | null => typeof encounterOrRead === 'function' ? encounterOrRead() : encounterOrRead;
  const targetOn = (encounter: Row | null): Row | null => {
    const floor = Array.isArray(encounter?.encounter_inventories_arr) ? encounter.encounter_inventories_arr : [];
    return floor.map(record).find(row => row && upper(readInventoryField(row, 'inventory_id')) === id) ?? null;
  };
  const encounter = encounterNow(), target = targetOn(encounter);
  if (!encounter) return false;
  if (!target) return false;
  const keys = ['v_max', 'v_current', 'v_temp'];
  const before = keys.map(key => String(readInventoryField(target, key) ?? ''));
  const initialVitality = record(record(target.computed)?.vitality);
  const read = async (key: string): Promise<unknown> => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([fetchObject(key), new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('object-vitality-read-timeout')), deadlineMs);
      })]);
    } finally { clearTimeout(timer); }
  };
  try {
    const source = heldInventory(encounter, id) ?? record(await read(id));
    if (!source || source.forbidden || upper(readInventoryField(source, 'inventory_id')) !== id || !record(source.inventory)) return false;
    const raw = record(source.inventory)!;
    const row: Row = { ...target, inventory: { ...record(target.inventory), ...raw } };
    const templateId = upper(readInventoryField(row, 'inventory_item_id'));
    if (!objectVitalityTemplate(row, encounter, definition)) {
      const template = record(await read(templateId));
      if (!template || template.forbidden || upper(readItemField(template, 'item_id')) !== templateId) return false;
      row.catalog = { ...record(row.catalog), [templateId]: template };
    }
    // A participant/base refresh may replace the encounter while the SELECT is in flight.
    const currentEncounter = encounterNow(), current = targetOn(currentEncounter);
    if (!current) return false;
    const newerVitality = record(record(current.computed)?.vitality);
    const changed = newerVitality !== initialVitality || keys.some((key, index) => String(readInventoryField(current, key) ?? '') !== before[index]);
    const fresh = changed ? resolveObjectVitality(current, currentEncounter, definition) : null;
    if (fresh) { installObjectVitality(current, fresh); return true; }
    const prepared: Row = { ...current, inventory: row.inventory,
      catalog: objectVitalityTemplate(current, currentEncounter, definition) ? current.catalog : row.catalog };
    const value = resolveObjectVitality(prepared, currentEncounter, definition, true)!;
    const columns = record(current.inventory)!;
    for (const key of keys)
      if (Object.prototype.hasOwnProperty.call(raw, key)) columns[key] = readInventoryField(source, key);
    current.catalog = prepared.catalog;
    installObjectVitality(current, value);
    return true;
  } catch { return false; }
}
